import { useActiveClassNamesAndStyles } from '@/hooks/useActiveClassNamesAndStyles';
import {
  EventScreenAction,
  selectScreenElement,
  updateScreenElement,
} from '@/providers/sharedActions';
import { Dispatch, useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScreenElementBoundingBox } from './ScreenElementBoundingBox';
import { DisplayImageElement } from './DisplayImageElement';
import { shouldMountScreenElementBoundingBox } from './shouldMountScreenElementBoundingBox';
import { EngagementMode, SharedScreenState } from '@/types/screen';

export function ScreenImageElement({
  elementId,
  editor,
  state,
  dispatch,
}: {
  elementId: string;
  editor: boolean;
  state: SharedScreenState;
  dispatch: Dispatch<EventScreenAction>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const preview = searchParams.get('preview') === 'true';
  const showBoundingBox = shouldMountScreenElementBoundingBox({
    editor,
    preview,
  });
  const [disableAnimation, setDisableAnimation] = useState(false);

  const element =
    state.savedTheme.stageElements?.[elementId] ||
    state.savedTheme.fanElements?.[elementId];
  const draftElement =
    state.draftTheme?.stageElements?.[elementId] ||
    state.draftTheme?.fanElements?.[elementId];

  let hasActiveEngagement =
    state.engagementMode === EngagementMode.Guide ||
    Boolean(
      state.activeEngagement && state.engagementMode === EngagementMode.Actual,
    );

  if (!editor) {
    hasActiveEngagement = Boolean(state.activeEngagement);
  }

  const { className, styles: activeStyles } = useActiveClassNamesAndStyles(
    element || ({} as any),
    hasActiveEngagement,
  );

  const draftImageUrl = draftElement?.imageUrl;
  const selected = state.selectedElementId === elementId;

  const { wrapperStyles, contentStyles } = useMemo(() => {
    const { width, height, top, left, ...rest } = activeStyles;
    const wrapperStyles = { width, height, top, left };
    const contentStyles = {
      ...rest,
      backgroundImage: `url(${draftImageUrl || element?.imageUrl || ''})`,
    };
    return { wrapperStyles, contentStyles };
  }, [activeStyles, draftImageUrl, element?.imageUrl]);

  const handleClick = useCallback(() => {
    if (editor) {
      if (!selected) {
        dispatch(selectScreenElement({ id: elementId }));
      }
    } else if (element?.linkHref) {
      window.open(element.linkHref, '_blank');
    }
  }, [editor, selected, elementId, dispatch, element?.linkHref]);

  if (!element) {
    return null;
  }

  return (
    <div
      ref={ref}
      data-name="IMAGE_STAGE_ELEMENT_WRAPPER"
      data-id={elementId}
      className={`absolute ${disableAnimation ? '' : 'transition-all duration-1000'} `}
      style={wrapperStyles}
    >
      <DisplayImageElement
        mode="background"
        dataName="IMAGE_STAGE_ELEMENT"
        dataId={elementId}
        className={`h-full w-full ${className} ${disableAnimation ? '' : 'transition-all duration-1000'} `}
        style={contentStyles}
        onClick={handleClick}
      />
      {showBoundingBox && (
        <ScreenElementBoundingBox
          selected={selected}
          onSelect={() => dispatch(selectScreenElement({ id: elementId }))}
          ref={ref}
          element={element}
          activeEngagement={hasActiveEngagement}
          onUpdate={(element) => dispatch(updateScreenElement({ element }))}
          onDragStart={() => setDisableAnimation(true)}
          onDragEnd={() => setTimeout(() => setDisableAnimation(false), 0)}
        />
      )}
    </div>
  );
}
